// Rotate in quarter turns and drag a rectangle to crop. Works on images and on
// the first page of a PDF (rendered by pdfjs); the result is saved as a JPEG.
import { useEffect, useRef, useState } from "react";
import { BlockStack, InlineStack, Button, ButtonGroup, Spinner, Text, Banner } from "@shopify/polaris";
import { loadEditableImage, renderEdit } from "./scannedDocs";

const MAX_W = 640;
const MAX_H = 520;
const MIN_CROP = 0.02; // ignore accidental clicks

function ImageEditor({ doc, saving, onCancel, onSave }) {
  const [image, setImage] = useState(null);
  const [error, setError] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [crop, setCrop] = useState(null); // {x, y, w, h} fractions of the rotated image
  const [size, setSize] = useState({ w: 0, h: 0 });
  const canvasRef = useRef(null);
  const drag = useRef(null);

  useEffect(() => {
    let alive = true;
    loadEditableImage(doc)
      .then((img) => alive && setImage(img))
      .catch((e) => alive && setError(e.message || String(e)));
    return () => {
      alive = false;
    };
  }, [doc]);

  // Redraw the preview whenever the rotation changes.
  useEffect(() => {
    if (!image || !canvasRef.current) return;
    const full = renderEdit(image, rotation, null);
    const scale = Math.min(MAX_W / full.width, MAX_H / full.height, 1);
    const w = Math.round(full.width * scale);
    const h = Math.round(full.height * scale);
    const c = canvasRef.current;
    c.width = w;
    c.height = h;
    c.getContext("2d").drawImage(full, 0, 0, w, h);
    setSize({ w, h });
  }, [image, rotation]);

  const turn = (deg) => {
    setRotation((r) => (r + deg + 360) % 360);
    setCrop(null);
  };

  const point = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };

  const onDown = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = point(e);
    setCrop(null);
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const a = drag.current;
    const b = point(e);
    setCrop({ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) });
  };
  const onUp = () => {
    drag.current = null;
    setCrop((c) => (c && (c.w < MIN_CROP || c.h < MIN_CROP) ? null : c));
  };

  const changed = rotation !== 0 || crop;

  if (error) return <Banner tone="critical">No se pudo abrir el documento: {error}</Banner>;

  return (
    <BlockStack gap="300">
      <InlineStack align="space-between" blockAlign="center">
        <ButtonGroup>
          <Button onClick={() => turn(-90)} disabled={!image || saving}>
            ⟲ Girar izquierda
          </Button>
          <Button onClick={() => turn(90)} disabled={!image || saving}>
            Girar derecha ⟳
          </Button>
          <Button onClick={() => setCrop(null)} disabled={!crop || saving}>
            Quitar recorte
          </Button>
        </ButtonGroup>
        <Text tone="subdued" variant="bodySm">
          Arrastra sobre la imagen para recortar
        </Text>
      </InlineStack>

      <div style={{ display: "flex", justifyContent: "center", background: "#f1f1f1", borderRadius: 8, padding: 8 }}>
        {!image ? (
          <div style={{ padding: 48 }}>
            <Spinner />
          </div>
        ) : (
          <div
            style={{ position: "relative", width: size.w, height: size.h, cursor: "crosshair", touchAction: "none" }}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerLeave={onUp}
          >
            <canvas ref={canvasRef} style={{ display: "block", userSelect: "none" }} />
            {crop && (
              <div
                style={{
                  position: "absolute",
                  left: `${crop.x * 100}%`,
                  top: `${crop.y * 100}%`,
                  width: `${crop.w * 100}%`,
                  height: `${crop.h * 100}%`,
                  border: "2px solid #2c6ecb",
                  boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
                  pointerEvents: "none",
                }}
              />
            )}
          </div>
        )}
      </div>

      {image?.wasPdf && (
        <Text tone="subdued" variant="bodySm">
          Es un PDF: se guardará la primera página como imagen JPG.
        </Text>
      )}
      {doc.status === "linked" && (
        <Text tone="subdued" variant="bodySm">
          El documento está vinculado: también se reemplazará la copia que ve el auto o la persona.
        </Text>
      )}

      <InlineStack gap="200" align="end">
        <Button onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" disabled={!image || !changed} loading={saving} onClick={() => onSave(image, rotation, crop)}>
          Guardar cambios
        </Button>
      </InlineStack>
    </BlockStack>
  );
}

export default ImageEditor;
